import {
  InferOutput,
  array,
  boolean,
  custom,
  email,
  maxLength,
  maxValue,
  minLength,
  minValue,
  nonEmpty,
  nullish,
  number,
  object,
  omit,
  optional,
  picklist,
  pipe,
  regex,
  string,
} from "valibot"

import { isIsraeliIdNumber } from "@finq/app-base/utils/profile/IsraeliValidations"
import {
  dateOfBirthSchema,
  generateDateOfBirthSchema,
  generateNameSchema,
  phoneNumberSchema,
} from "@finq/app-base/utils/user-details/custom-validations"

export const userDetailsBaseSchema = object({
  firstName: generateNameSchema("firstName"),
  lastName: generateNameSchema("lastName"),
  phoneNumber: phoneNumberSchema,
  phoneNumberCountryCode: string(),
  dateOfBirth: dateOfBirthSchema,
  genderPronoun: picklist(["F", "M"]),
  gender: picklist(["F", "M"]),
  idNumber: pipe(
    string("validations.idNumber"),
    nonEmpty("validations.idNumber"),
    custom((value) => isIsraeliIdNumber(value as string), "validations.idNumberFormat")
  ),
  dateOfIssue: string("validations.dateOfIssue"),
  employeeStatus: picklist([
    "employed",
    "self_employed",
    "employed_and_self_employed",
    "controlling",
    "retired",
    "unemployed",
  ]),
  recentEmploymentChange: boolean(),
  jobOccupation: string("validations.jobOccupation"),
  publicPosition: boolean(),
  healthChange: boolean(),
  smoking: boolean(),
  otherBeneficiaries: picklist(["self", "family", "others"]),
  refusedServices: boolean(),
  familyStatus: picklist(["single", "married", "publicly_known", "divorcee", "separated", "widower"]),
  childrenAmount: pipe(optional(number(), 0), minValue(0), maxValue(20)),
  financialSupport: picklist(["no_support", "support"]),
  investmentsBackground: picklist([
    "lack_of_experience",
    "little_experience",
    "not_very_experienced",
    "regularly_updated",
    "expert",
  ]),

  investmentsPeriod: picklist([1, 2, 3]),
  responseStrategy: picklist([1, 2, 3]),
  riskToleranceScenario: picklist([1, 2, 3]),
  occupationLifestyleRisks: nullish(boolean()),
  occupationLifestyleRisksExplanation: string("validations.explanation"),
  verificationDate: nullish(number()),
  signedContract: boolean(),
  agreedPensionRequiredTerms: nullish(boolean()),
  significantHealthEvent: pipe(
    boolean(),
    custom((value) => !value)
  ),
  updatePensionDepositPercentage: boolean(),
  extremeHobbies: optional(array(string("validations.extremeHobbies"))),
  pensionProvisionAmount: number("validations.pensionProvisionAmount"),
  educationReportedSalary: number("validations.educationReportedSalary"),
  sectionFourteen: boolean(),
  pensionSalaryVerification: string("validations.pensionSalaryVerification"),
  kerenHishtalmutVerification: string("validations.kerenHishtalmutVerification"),
  pensionIncreaseInterest: boolean(),
  additionalAllowanceOrPension: boolean(),
  additionalAllowanceOrPensionAmount: number("validations.additionalAllowanceOrPensionAmount"),

  contingentBeneficiary: picklist([
    "equivalent_other_beneficiaries",
    "relative_to_share",
    "beneficiary_legal_heirs",
    "cannot_receive",
    "other",
  ]),

  extendedForeignStay: boolean(),
  extendedForeignStayCountry: optional(string()),
  primaryBeneficiary: picklist(["choose_beneficiaries", "as_will", "as_low"]),

  employerContactEmail: pipe(
    string("validations.mail_empty"),
    nonEmpty("validations.mail_empty"),
    email("validations.email_not_valid")
  ),

  employerName: string("validations.employerName"),
  employerIdentificationNumber: string("validations.employerIdentificationNumber"),
  employerAddress: string("validations.employerAddress"),

  city: pipe(string("validations.city"), nonEmpty("validations.city")),
  street: pipe(string("validations.street"), nonEmpty("validations.street")),
  houseNumber: pipe(string("validations.houseNumber"), nonEmpty("validations.houseNumber")),
  postalCode: optional(nullish(pipe(string(), regex(/^\d{5}(\d{2})?$/, "validations.postalCode")))),

  sportsInvolvement: boolean(),
  airWorker: boolean(),
})

export const baseSpouseSchema = object({
  id: optional(nullish(number(), null)),
  firstName: generateNameSchema("firstName"),
  lastName: generateNameSchema("lastName"),
  idNumber: userDetailsBaseSchema.entries.idNumber,
  phoneNumber: userDetailsBaseSchema.entries.phoneNumber,
  phoneNumberCountryCode: userDetailsBaseSchema.entries.phoneNumberCountryCode,
})
export type BaseSpouseSchema = InferOutput<typeof baseSpouseSchema>

export const spouseSchema = object({
  ...baseSpouseSchema.entries,
  dateOfBirth: dateOfBirthSchema,
  gender: userDetailsBaseSchema.entries.gender,
})
export type SpouseSchema = InferOutput<typeof spouseSchema>

export const childrenSchema = object({
  ...omit(baseSpouseSchema, ["phoneNumber"]).entries,
  id: optional(nullish(number(), null)),
  dateOfBirth: generateDateOfBirthSchema({ min: 0 }),
  disability: nullish(boolean(), false),
  disabilityRecognitionDate: generateDateOfBirthSchema({ min: 0 }),
})
export type ChildrenSchema = InferOutput<typeof childrenSchema>

export const beneficiariesSchema = object({
  ...baseSpouseSchema.entries,
  beneficiaryRelation: string("validations.beneficiaryRelation"),
  dateOfBirth: dateOfBirthSchema,
  idNumber: userDetailsBaseSchema.entries.idNumber,
  gender: userDetailsBaseSchema.entries.gender,
  phoneNumber: phoneNumberSchema,
  phoneNumberCountryCode: string(),
  address: string("validations.address"),
  beneficiaryPercentage: number("validations.beneficiaryPercentage"),
})
export type BeneficiariesSchema = InferOutput<typeof beneficiariesSchema>

export const employerSchema = object({
  id: optional(nullish(number(), null)),
  employerName: string("validations.employerName"),
  employerIdentificationNumber: string("validations.employerIdentificationNumber"),
  employerAddress: string("validations.employerAddress"),
  employerContactEmail: pipe(
    string("validations.mail_empty"),
    nonEmpty("validations.mail_empty"),
    email("validations.email_not_valid")
  ),
  employerContactName: pipe(
    string("validations.fullName"),
    nonEmpty("validations.fullName"),
    minLength(1, "validations.fullName_too_short"),
    maxLength(60, "validations.fullName_too_long"),
    custom(
      (value) =>
        (value as string)?.split(" ").filter((v) => Boolean(v) && new RegExp(/^\p{L}.*\p{L}$/u).test(v))
          .length > 1,
      "validations.fullName_contains_space"
    )
  ),
  employerContactPhone: phoneNumberSchema,
  employerContactPhoneCountryCode: string(),
})
export type EmployerSchema = InferOutput<typeof employerSchema>

export const userDetailsSchema = object({
  ...userDetailsBaseSchema.entries,
  spouse: spouseSchema,
  employer: employerSchema,
  children: array(childrenSchema),
  beneficiaries: array(beneficiariesSchema),
})
export type UserDetails = InferOutput<typeof userDetailsSchema>
